'use client';

import { c as _c } from "react/compiler-runtime";
import { useRef, useState } from 'react';
import { LuX } from 'react-icons/lu';
import { GoogleMapsAutocomplete } from '~/features/google/maps';
import { useI18n } from '~/features/i18n';
import type { WidgetLocation } from '../types';
type Props = {
  location: WidgetLocation | null;
  /** Passing `null` clears the location (X button) so the map returns to its overview. */
  onLocationChange: (location: WidgetLocation | null) => void;
  /**
   * The user pressed "Bereken je transportkosten" and has not picked a place yet. Swaps the
   * placeholder for an instruction — on touch the CTA opened the picker with nothing else
   * changing, so it looked like nothing had happened.
   */
  isAwaitingLocation?: boolean;
  className?: string;
};

/**
 * Location picker for the widget: reuses the same Google Places autocomplete the
 * `/teambuildings` filter uses. Picking a place resolves coordinates; the clear
 * (X) button resets the location so the map recenters on the availability overview.
 * The radius selector lives in checkout, not here.
 */
export const LocationInputRow: React.FC<Props> = t0 => {
  const $ = _c(21);
  const {
    location,
    onLocationChange,
    isAwaitingLocation,
    className
  } = t0;
  const i18n = useI18n();
  const [query, setQuery] = useState(location?.query ?? "");
  const latestQuery = useRef(query);
  const [syncedExternalQuery, setSyncedExternalQuery] = useState(location?.query ?? null);
  if (location?.query != null && location.query !== syncedExternalQuery) {
    setSyncedExternalQuery(location.query);
    setQuery(location.query);
  }
  let t1;
  if ($[0] !== i18n || $[1] !== isAwaitingLocation) {
    t1 = isAwaitingLocation ? i18n._("pdp.location.input.awaiting", undefined, {
      message: "Type a town or postcode\u2026"
    }) : i18n._("pdp.location.input.placeholder", undefined, {
      message: "Enter your location\u2026"
    });
    $[0] = i18n;
    $[1] = isAwaitingLocation;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = selected => {
      setQuery(selected);
      latestQuery.current = selected;
    };
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== onLocationChange) {
    t3 = t4 => {
      const {
        latitude,
        longitude
      } = t4;
      onLocationChange({
        query: latestQuery.current,
        lat: Number(latitude),
        lng: Number(longitude)
      });
    };
    $[4] = onLocationChange;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] !== query || $[7] !== t1 || $[8] !== t3) {
    t4 = <GoogleMapsAutocomplete className="border-0 py-2.5" value={query} placeholder={t1} onChange={t2} onResolve={t3} />;
    $[6] = query;
    $[7] = t1;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== i18n || $[11] !== location || $[12] !== onLocationChange || $[13] !== query) {
    t5 = (!!query || !!location) && <button type="button" onClick={() => {
      setQuery("");
      latestQuery.current = "";
      onLocationChange(null);
    }} data-testid="pdp-location-clear" aria-label={i18n._("pdp.location.input.clear", undefined, {
      message: "Clear location"
    })} className="flex min-h-10 items-center rounded-r-full pr-4 pl-2 text-blue-900/50 transition-colors hover:text-blue-900"><LuX className="size-4" /></button>;
    $[10] = i18n;
    $[11] = location;
    $[12] = onLocationChange;
    $[13] = query;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t4 || $[16] !== t5) {
    t6 = <div className="flex w-full items-stretch overflow-hidden rounded-full bg-white shadow-lg">{t4}{t5}</div>;
    $[15] = t4;
    $[16] = t5;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  let t7;
  if ($[18] !== className || $[19] !== t6) {
    t7 = <div className={className}>{t6}</div>;
    $[18] = className;
    $[19] = t6;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
};